/**
 * ContextoView -- QUIEN TIENE LLAVE DE MI CONTEXTO.
 *
 * MISION CONTEXT-PLATFORM-V1-001, tarea CTX-M2-09.
 *
 * AUDIENCIA: Josse y los devs, en el panel de escritorio (audience 'nexo' y
 * rol dev/admin). NO es pantalla de tablet ni de colaborador: aqui se ve quien
 * puede leer el contexto de uno, y se le puede quitar el permiso.
 *
 * SOLO LECTURA Y REVOCAR (limite puesto por Ares en el ASSIGN #10246). Emitir
 * llaves nuevas no se hace desde aqui, y la validacion de lo que el asistente
 * cree es otra tarea (CTX-M2-10).
 *
 * LA LLAVE NUNCA SE MUESTRA. La API no la devuelve -- solo `keyPrefix`, 13
 * caracteres -- y esta vista tampoco la pide.
 *
 * QUE LISTA, DICHO SIN ADORNOS: la API filtra por sujeto solo si se le pasa el
 * parametro, y el panel todavia no sabe sacar del token el id de quien mira
 * (getJwtUserId lee sub/uid/user_id y para estos tokens devuelve 'anon'; leerlo
 * de `nameid` es CTX-M2-16 y espera una decision de Josse). Asi que esta
 * pantalla lista TODAS las llaves emitidas y LO DICE en pantalla, en vez de
 * llamarse "las mias" y mentir. Cuando el id se pueda leer, la lista se filtra
 * sola y la etiqueta cambia.
 */

import { ContextApiClient, SCOPES, RECETAS, revisarConjunto, cambioDeConsentimiento, TEXTO_MINIMO_CONSENTIMIENTO } from '../../infrastructure/api/ContextApiClient.js';
import { sumarReceta, cuantosNuevos, resumenDePermisos, llavesVisibles } from '../../application/contexto/formularioLlave.js';
import { destinoDe, permisosEnPalabras, borradorDeConsentimiento } from '../../application/contexto/propuestasLlave.js';
import { AppConfig } from '../../config.js';
import { resolveUserAudience } from '../shared/useAudience.js';
import { getJwtUserId } from '../shared/jwtUserId.js';
import { LearningProfileApiClient } from '../../infrastructure/api/LearningProfileApiClient.js';

const C = {
  texto: '#e8e8f0',
  suave: '#8a8aa8',
  linea: '#2a2a3e',
  bien: '#2f6b40',
  ojo: '#d29922',
  mal: '#ff9a9a',
  acento: '#7fe8d8'
};

/** Solo dev/admin, y ademas audiencia interna. El filtro por audiencia no basta:
 *  resolveUserAudience() devuelve 'nexo' tambien para un member. */
function puedeVer() {
  const rol = AppConfig.role;
  const aud = resolveUserAudience();
  return ['dev', 'admin'].includes(rol) && (aud === 'all' || aud === 'nexo');
}

// Deuda 4: el escape local no cambiaba la comilla simple; ahora es el compartido.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';

/** Fecha corta y legible; null si no hay. */
function fecha(v) {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()}`;
}

/** "hace 3 dias" / "nunca". Devuelve tambien si esta vencida. */
function cuando(v) {
  const f = fecha(v);
  if (!f) return null;
  const dias = Math.floor((Date.now() - new Date(v).getTime()) / 86400000);
  if (dias <= 0) return `${f} (hoy)`;
  if (dias === 1) return `${f} (ayer)`;
  return `${f} (hace ${dias} dias)`;
}

function estadoDe(c) {
  if (c.revocada) return { txt: 'QUITADA', color: C.mal, viva: false };
  const vence = c.vence ? new Date(c.vence).getTime() : null;
  if (vence && vence < Date.now()) return { txt: 'VENCIDA', color: C.suave, viva: false };
  return { txt: 'ACTIVA', color: C.bien, viva: true };
}

function pantallaSinDatos(motivo, detalle) {
  const titulos = {
    SIN_SESION: 'Tu sesion no alcanza para ver esto',
    PROHIBIDO: 'Esta pantalla no es para tu usuario',
    NO_PUBLICADO: 'La API todavia no publica las llaves',
    DEV_SIN_API: 'Estas en el panel de desarrollo',
    SIN_RED: 'No pudimos hablar con la API',
    ERROR_API: 'La API respondio con un error'
  };
  return '<div style="padding:26px;text-align:center;border:1px solid ' + C.linea + ';border-radius:8px">'
    + '<div style="font-size:1.5rem;margin-bottom:8px">&#128273;</div>'
    + '<div style="color:' + C.texto + ';font-size:.9rem;margin-bottom:6px">' + esc(titulos[motivo] || 'No se pudo cargar') + '</div>'
    + '<div style="color:' + C.suave + ';font-size:0.875rem">' + esc(detalle || '') + '</div></div>';
}

function filaHtml(c, miId) {
  const est = estadoDe(c);
  const mia = miId && miId !== 'anon' && c.sujeto && c.sujeto === miId;
  const uso = cuando(c.ultimoUso);
  const vence = fecha(c.vence);
  const permisos = c.permisos.length ? c.permisos.join(', ') : 'sin permisos declarados';
  return '<div data-cred-fila="' + esc(c.id) + '" style="border:1px solid ' + C.linea + ';border-radius:8px;padding:12px;margin-bottom:10px">'
    + '<div style="display:flex;justify-content:space-between;align-items:flex-start;gap:10px;flex-wrap:wrap">'
    + '<div style="min-width:200px">'
    + '<div style="color:' + C.texto + ';font-size:.9rem">' + esc(c.agente || '(sin agente)')
    + (mia ? ' <span style="color:' + C.acento + ';font-size:0.875rem">&mdash; tuya</span>' : '') + '</div>'
    + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:2px">' + esc(c.nombre || 'sin nombre') + '</div>'
    + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:4px;font-family:monospace">llave ' + esc(c.prefijo || '(sin prefijo)') + '&hellip;</div>'
    + '</div>'
    + '<div style="text-align:right"><span style="color:' + est.color + ';font-size:0.875rem;font-weight:bold">' + est.txt + '</span>'
    + (vence ? '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:2px">vence ' + esc(vence) + '</div>' : '')
    + '</div></div>'
    + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:8px">Puede: ' + esc(permisos)
    + (c.riesgoMaximo ? ' &middot; hasta riesgo ' + esc(c.riesgoMaximo) : '') + '</div>'
    + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:2px">Ultima vez que la uso: '
    + (uso ? esc(uso) : '<span style="color:' + C.ojo + '">nunca se ha usado</span>') + '</div>'
    + (est.viva
      ? '<div style="margin-top:10px"><button class="btn-ghost btn-sm" data-quitar="' + esc(c.id) + '" style="font-size:0.875rem">Quitarle el acceso</button></div>'
      : (c.revocada ? '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:8px">Se la quitaron el ' + esc(fecha(c.revocada) || '') + '</div>' : ''))
    + '<div data-quitar-caja="' + esc(c.id) + '" hidden style="margin-top:10px;border-top:1px solid ' + C.linea + ';padding-top:10px">'
    + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-bottom:6px">Por que se la quitas? Queda escrito en el rastro.</div>'
    + '<input type="text" class="form-input form-input-sm" data-motivo="' + esc(c.id) + '" placeholder="ya no trabaja con nosotros" style="width:100%;max-width:340px">'
    + '<div style="margin-top:8px;display:flex;gap:8px">'
    + '<button class="btn-primary btn-sm" data-confirmar="' + esc(c.id) + '" style="font-size:0.875rem">Quitar</button>'
    + '<button class="btn-ghost btn-sm" data-cancelar="' + esc(c.id) + '" style="font-size:0.875rem">Cancelar</button></div>'
    + '<div data-aviso="' + esc(c.id) + '" style="color:' + C.mal + ';font-size:0.875rem;margin-top:6px"></div>'
    + '</div></div>';
}

/**
 * EL FORMULARIO DE DAR ACCESO (CTX-M2-19).
 *
 * La pantalla sabia VER y QUITAR; le faltaba DAR. Josse fue a emitir la llave de
 * su tutor donde la guia decia y no pudo: hubo que emitirsela por SQL, o sea que
 * cada llave nueva pasaba por Ares y la mitad del control vivia fuera de aqui.
 *
 * LO QUE MAS IMPORTA DE ESTE FORMULARIO NO ES QUE EXISTA: es que los permisos
 * salen de una LISTA y no de un campo de texto. Hoy conviven dos grafias y
 * elegir la vieja da un 403 MUDO -- le paso a Josse: su tutor leia bien y
 * fallaba al registrar, sin decir por que hasta que el propio tutor lo dijo.
 *
 * Y AQUI ME CORRIJO, porque lo que escribi debajo era falso. Yo habia puesto que
 * "una lista mata esa clase de error para siempre". Mata los TYPOS. No mata el
 * error que de verdad rompio el tutor de Josse, que fue elegir bien escrito un
 * conjunto INCOMPLETO: tres permisos correctos, y el cuarto ausente. Q se lo
 * corrigio a Ares con esas palabras (#10714) y aplica igual a esta frase mia.
 *
 * Para ESE error esta la RECETA (CTX-M2-20): cuando el conjunto correcto depende
 * de conocer una historia -- aqui, que conviven dos generaciones de nombres y que
 * hay DOS endpoints de cerrar sesion con permisos distintos --, se ofrece el
 * conjunto y no los ingredientes. El detalle medido esta en ContextApiClient.js,
 * arriba de RECETAS.
 */
function formularioHtml() {
  const campo = (etiqueta, attr, extra) => ''
    + '<label style="display:block;margin-bottom:8px">'
    + '<span style="display:block;font-size:0.875rem;color:' + C.suave + ';margin-bottom:3px">' + etiqueta + '</span>'
    + '<input ' + attr + ' style="width:100%;box-sizing:border-box;padding:6px 8px;border-radius:6px;'
    + 'border:1px solid ' + C.linea + ';background:#0d1117;color:' + C.texto + ';font-size:0.875rem">'
    + '</label>' + (extra || '');

  const permisos = SCOPES.map((s) => ''
    + '<label style="display:flex;gap:8px;align-items:flex-start;padding:6px 8px;border-radius:6px;'
    + 'border:1px solid ' + C.linea + ';margin-bottom:6px;cursor:pointer">'
    + '<input type="checkbox" data-scope value="' + esc(s.id) + '" style="margin-top:2px">'
    + '<span style="flex:1">'
    + '<span style="font-size:0.875rem;color:' + C.texto + '">' + esc(s.que) + '</span>'
    + '<span style="display:block;font-family:monospace;font-size:0.875rem;color:' + C.suave + '">'
    + esc(s.id) + ' &middot; riesgo ' + esc(s.riesgo) + '</span>'
    // LA RUTA QUE HABILITA, no un adjetivo. Quien emite puede cruzarla con lo
    // que el agente de verdad llama; sin esto hay que creerle a mi resumen.
    + '<span style="display:block;font-family:monospace;font-size:0.875rem;color:' + C.suave + ';opacity:.75">'
    + esc(s.donde || '') + '</span>'
    + '</span></label>').join('');

  // LAS RECETAS VAN ARRIBA DE LA LISTA, no abajo: quien viene a emitir la llave
  // de un tutor tiene que toparse con el conjunto ANTES de empezar a marcar
  // casillas una por una. Abajo seria un premio de consolacion.
  const recetas = RECETAS.map((r) => ''
    + '<button type="button" data-receta="' + esc(r.id) + '" '
    + 'style="display:block;width:100%;text-align:left;padding:8px 10px;border-radius:6px;'
    + 'border:1px dashed ' + C.linea + ';background:#12181f;color:' + C.texto + ';'
    + 'font-size:0.875rem;cursor:pointer;margin-bottom:6px">'
    + '&#10022; ' + esc(r.nombre)
    + '<span style="display:block;font-size:0.875rem;color:' + C.suave + ';margin-top:2px">'
    + esc(r.para) + ' &middot; marca ' + r.scopes.length + ' permisos</span>'
    + '</button>').join('');

  return '<div data-ctx-form hidden style="border:1px solid ' + C.linea + ';border-radius:8px;padding:14px;margin-top:12px">'
    + '<p style="margin:0 0 10px;font-size:0.875rem;color:' + C.suave + '">'
    + 'La llave completa se muestra <strong>una sola vez</strong> y no se guarda en ninguna parte. '
    + 'Quien la pierda, la quita y emite otra.</p>'
    + campo('Codigo del asistente', 'data-f="agente" placeholder="p. ej. GPT-TUTOR-001"')
    + campo('Nombre para reconocerla', 'data-f="nombre" placeholder="p. ej. Tutor de Josse"')
    // DONDE QUEDA EL SECRETO, OBLIGATORIO (CTX-M2-22, hallazgo de Argy-Q).
    //
    // LA ASIMETRIA que lo justifica: esta MISMA pantalla, al REVOCAR, ya exige
    // motivo -- "escribe por que la quitas: sin motivo no queda rastro util" --
    // y al EMITIR no pedia nada. Le pediamos rastro al final del ciclo y no al
    // principio, que es donde se pierde.
    //
    // Y NO ES HIPOTETICO: de nueve credenciales emitidas en dos dias, DOS
    // quedaron vivas sin que su portador tuviera el secreto. Argy-Q lo dijo de
    // si mismo: "no me llegaron: ni en mi contexto, ni en archivo, ni en la
    // bitacora". Estuvieron asi mas de veinte horas sin que nadie lo notara.
    // Una llave cuyo secreto nadie guardo es acceso vivo que nadie puede usar
    // y que igual cuenta como acceso vivo.
    //
    // ESTO SE VOLVIO URGENTE AHORA y no antes: hasta hoy emitir pasaba por
    // Ares, que guardaba el secreto en archivo. Desde CTX-M2-19 y M2-20 emite
    // cualquiera desde aqui -- que era el objetivo -- y con eso se mudo tambien
    // el descuido.
    // LO QUE SE ESCRIBE AQUI SE PUBLICA (aviso de Argy-Q en su dictamen al #277).
    //
    // Este campo termina dentro del nombre de la credencial, y ese nombre viaja
    // a la bitacora... cuyo GET es ANONIMO. Es hallazgo del propio Q (#10304):
    // cualquiera que sepa la URL lee lo que aqui se ponga. Asi que el campo
    // puede decir DONDE quedo el secreto, y nunca el secreto.
    //
    // Y el matiz de Q, que es el que de verdad importa y por eso esta en la
    // pantalla y no solo en un comentario: una ruta en el portatil de Josse no
    // le sirve a nadie de afuera; nombrar una maquina alcanzable desde internet
    // SI le sirve a quien ya tenga un pie adentro.
    + campo('Donde queda el secreto', 'data-f="donde" placeholder="p. ej. gestor de claves de Josse / archivo del VPS"',
      '<div style="font-size:0.875rem;color:' + C.suave + ';margin:-4px 0 8px">'
      + 'Obligatorio. La llave se muestra <strong>una sola vez</strong>: si nadie anota donde quedo, '
      + 'el acceso sigue vivo y no lo puede usar nadie.<br>'
      + '<strong style="color:' + C.ojo + '">Nunca pegues aqui el secreto.</strong> Esto se publica: '
      + 'termina en el nombre de la llave y de ahi en la bitacora, que se lee sin credencial. '
      + 'Y preferi el sitio menos util para un extrano: una carpeta de tu portatil no le sirve a nadie '
      + 'de afuera; el nombre de una maquina que se alcanza por internet, si.</div>')
    + campo('Persona (subject id)', 'data-f="sujeto" placeholder="uuid; deja vacio si es de negocio"')
    + campo('Negocio (business id)', 'data-f="negocio" placeholder="deja vacio si es de una persona"')
    + campo('Dias de vigencia', 'data-f="dias" type="number" min="1" max="90" value="90"')
    + '<div style="margin:12px 0 6px;font-size:0.875rem;color:' + C.suave + '">'
    + 'Atajo para un caso conocido</div>'
    + recetas
    // PANEL-ATAJOS-LLAVE-T1: los atajos SUMAN; borrar es este boton aparte.
    + '<button type="button" data-limpiar-permisos style="padding:5px 10px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer;margin:2px 0 4px">Limpiar permisos</button>'
    + '<div style="margin:10px 0 6px;font-size:0.875rem;color:' + C.suave + '">Que va a poder hacer</div>'
    + permisos
    // Lo que de verdad se va a pedir, antes de pedirlo (se actualiza con cada casilla y cada atajo).
    + '<div data-resumen-permisos style="font-size:0.875rem;color:' + C.texto + ';margin:8px 0 2px;line-height:1.5">' + esc(resumenDePermisos([])) + '</div>'
    + '<div data-form-aviso style="font-size:0.875rem;min-height:16px;margin:6px 0"></div>'
    + '<button data-emitir style="padding:7px 14px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:#1f6feb;color:#fff;font-size:0.875rem;cursor:pointer">Emitir llave</button>'
    + '</div>'
    + '<div data-llave-nueva hidden style="border:1px solid #d29922;border-radius:8px;padding:14px;margin-top:12px;background:#1c1710">'
    + '<div style="font-size:0.875rem;color:#f0c674;margin-bottom:6px">'
    + '&#9888; Esta es la unica vez que veras la llave completa. Copiala ahora.</div>'
    + '<code data-llave-txt style="display:block;word-break:break-all;font-size:0.875rem;color:' + C.texto + ';'
    + 'background:#0d1117;padding:8px;border-radius:6px;margin-bottom:8px"></code>'
    + '<button data-copiar style="padding:6px 12px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Copiar</button>'
    + '<span data-copiado style="font-size:0.875rem;color:' + C.bien + ';margin-left:8px"></span>'
    + '</div>';
}
/**
 * CTX-M2-10 — LA DECISION SOBRE LO QUE UN ASISTENTE CREE.
 *
 * AUDIENCIA (regla #5638): JOSSE, dueno, decidiendo sobre lo que un asistente
 * cree de una PERSONA -- su hijo, un colaborador --, en el panel de escritorio.
 * No es una lista de administracion: es una decision sobre alguien. Por eso
 * cada fila muestra EN QUE SE BASA el asistente antes de que se pulse nada, y
 * por eso la nota va al lado del rechazo y no en un dialogo aparte.
 *
 * POR QUE EXISTE, en una linea de Ares (#10785): "sin ella el ciclo queda a
 * medias: el tutor ESTIMA y nadie CONFIRMA".
 *
 * QUE SE FUE DE AQUI (CTX-P2, 7-sep): el PROGRESO. Nivel, tema actual y temas
 * dominados se ven ahora en Usuarios, pestana Aprendizaje, junto con las
 * sesiones del tutor. Aqui queda la DECISION, que no es progreso: es una accion
 * sobre lo que un asistente propuso, y encaja con el resto de esta pantalla.
 *
 * POR QUE LA DECISION NO SE MOVIO TAMBIEN: en Usuarios el sujeto se resuelve
 * por el email de quien tiene la sesion, asi que solo alcanza a la propia fila.
 * Aqui se puede pegar el uuid de CUALQUIER persona, que es la unica forma de
 * validar el perfil de un hijo o de un colaborador mientras la API no publique
 * un endpoint que enumere sujetos. Moverla habria quitado esa capacidad sin
 * darla en ningun otro sitio.
 *
 * NO HAY SELECTOR DE PERSONA, Y SE DICE. No existe endpoint que liste los
 * sujetos con perfil: lo busque en los controladores. Un desplegable vacio
 * seria peor que dos campos honestos, asi que se piden los dos datos y la
 * pantalla explica de donde salen. El dia que la API publique esa lista, esto
 * se cambia por un selector y nada mas.
 */
function seccionValidarHtml() {
  const campo = (etiqueta, attr, ancho) => ''
    + '<label style="display:block;flex:1 1 ' + ancho + '">'
    + '<span style="display:block;font-size:0.875rem;color:' + C.suave + ';margin-bottom:3px">' + etiqueta + '</span>'
    + '<input ' + attr + ' style="width:100%;box-sizing:border-box;padding:6px 8px;border-radius:6px;'
    + 'border:1px solid ' + C.linea + ';background:#0d1117;color:' + C.texto + ';font-size:0.875rem">'
    + '</label>';

  return '<div class="detail-section" style="margin-top:18px">'
    + '<h2 style="margin:0;font-size:1rem;color:' + C.texto + '">&#129504; Lo que un asistente cree</h2>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">'
    + 'Un asistente <strong>estima</strong>; confirmarlo es tuyo. Aqui ves en que se basa, '
    + 'lo aceptas o lo rechazas con una nota, y el perfil sube de version con tu nombre y la fecha. '
    + 'Una llave de asistente <strong>no puede</strong> validar lo que ella misma estimo: la API lo rechaza.</p>'
    + '<div style="display:flex;gap:10px;align-items:flex-end;margin-top:10px;flex-wrap:wrap">'
    + campo('Persona (subject id)', 'data-v="sujeto" placeholder="uuid de la persona"', '240px')
    + campo('Programa', 'data-v="programa" placeholder="p. ej. ingles"', '140px')
    + '<button data-v-ver style="padding:7px 14px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Ver</button>'
    + '</div>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin:6px 0 0">'
    + 'No hay lista de personas todavia: la API no publica un endpoint que las enumere, '
    + 'asi que se piden los dos datos en vez de mostrar un desplegable vacio.</p>'
    + '<div data-v-aviso style="font-size:0.875rem;min-height:18px;margin:8px 0"></div>'
    + '<div data-v-cuerpo></div>'
    + '</div>';
}

/** El perfil como cabecera: version, quien lo valido y cuando. */
function perfilHtml(p) {
  if (!p) {
    return '<p style="font-size:0.875rem;color:' + C.suave + '">'
      + 'Esa persona no tiene perfil todavia en ese programa. '
      + 'El perfil nace cuando un asistente registra la primera observacion.</p>';
  }
  // El rastro NO se rellena con "sistema" ni con un guion mudo: si nadie lo ha
  // validado, eso ES el dato, y es justo el que esta pantalla viene a cambiar.
  const rastro = p.validadoPor
    ? 'validado por <strong>' + esc(p.validadoPor) + '</strong>' + (p.validadoEl ? ' el ' + esc(fecha(p.validadoEl)) : '')
    : '<strong>nadie lo ha validado todavia</strong>';
  // EL PROGRESO YA NO SE PINTA AQUI (CTX-P2, decision D10 del 7-sep).
  //
  // Nivel, tema actual y temas dominados vivian en esta tarjeta Y en la pestana
  // Skills de Usuarios Y en Capacitacion. Tres sitios diciendo "como va" es como
  // empiezan a discrepar, y el dueno acaba creyendole al que tenga mas a mano.
  // Ahora eso vive en UNA pantalla: Usuarios, pestana Aprendizaje.
  //
  // Lo que SI se queda es el rastro -- version y quien valido --, porque no es
  // progreso: es lo que hace falta para tomar la decision que esta pantalla
  // pide. Y `nivel` deja de calcularse: pintaba un JSON crudo con llaves y
  // comillas, que en Usuarios se lee como "A2 CEFR".
  return '<div style="border:1px solid ' + C.linea + ';border-radius:8px;padding:10px 12px;margin-bottom:10px">'
    + '<div style="font-size:0.875rem;color:' + C.texto + '">Perfil version <strong>' + esc(p.version) + '</strong> &middot; ' + rastro + '</div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">'
    + 'Como va esta persona se ve en <a href="#/users" style="color:#58a6ff">Usuarios</a>, pestana Aprendizaje.'
    + '</div></div>';
}

/**
 * UNA OBSERVACION PENDIENTE.
 *
 * La confianza se dice en el idioma del dueno y NO se inventa: si el asistente
 * no la declaro, se dice eso -- no un 0, que se leeria como "esta segurisimo de
 * que no". Es la diferencia entre no saber y saber que no.
 */
function pendienteHtml(o) {
  const conf = o.confianza === null
    ? '<span style="color:' + C.suave + '">sin confianza declarada</span>'
    : 'confianza ' + esc(Math.round(o.confianza * 100)) + '%';
  return '<div data-obs="' + esc(o.id) + '" style="border:1px solid ' + C.linea + ';border-radius:8px;'
    + 'padding:10px 12px;margin-bottom:8px">'
    + '<div style="display:flex;gap:10px;align-items:flex-start;flex-wrap:wrap">'
    + '<div style="flex:1 1 240px">'
    + '<div style="font-size:0.875rem;color:' + C.texto + '">' + esc(o.metrica || o.tipo || 'observacion')
    + (o.valor ? ': <strong>' + esc(o.valor) + '</strong>' : '') + '</div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:3px">'
    + (o.tema ? esc(o.tema) + ' &middot; ' : '') + conf
    + (o.agente ? ' &middot; lo dijo ' + esc(o.agente) : '')
    + (o.cuando ? ' &middot; ' + esc(fecha(o.cuando)) : '')
    + '</div></div>'
    + '<div style="display:flex;gap:6px">'
    + '<button data-dec="accept" style="padding:5px 10px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Aceptar</button>'
    + '<button data-dec="reject" style="padding:5px 10px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Rechazar</button>'
    + '</div></div>'
    + '<input data-nota hidden placeholder="por que lo rechazas (queda en el rastro)" '
    + 'style="width:100%;box-sizing:border-box;margin-top:8px;padding:6px 8px;border-radius:6px;'
    + 'border:1px solid ' + C.linea + ';background:#0d1117;color:' + C.texto + ';font-size:0.875rem">'
    + '</div>';
}

// LA SALIDA VIVE FUERA DE LAS DOS RAMAS (correccion de Argy-Q, #11122, y de
// Ares, #11123).
//
// En el PR #286 puse el enlace de volver DENTRO del if (!puedeVer()), que
// termina en return. O sea que la salida le quedaba a quien NO puede usar la
// pantalla, y el dueno -que si puede- seguia encerrado. Y esta es justo la
// pantalla de llaves que Josse abrio esa noche.
//
// Lo que lo hizo posible: las dos ramas pintan el MISMO titulo, asi que mi
// parcheador tomo la primera que encontro. Y mi asercion -que quedara
// exactamente un enlace- se cumplio sin ayudar: contaba apariciones, no en que
// rama caian.
//
// Por eso ahora es una CONSTANTE y no una linea repetida: definida una vez,
// usada en las dos ramas. Si manana aparece una tercera, quien la escriba ve la
// constante ahi al lado y la usa. Una linea suelta se olvida; una constante que
// ya existe se copia.
const VOLVER = regresoHtml();

/**
 * LO QUE LA PERSONA CONSIENTE (MCP-NEXO-OAUTH-T1, Ares #23490; ruta de nexo-api PR 447).
 *
 * AUDIENCIA: Josse, en el panel de escritorio, con su sesion de admin. Una llave solo puede pedir lo que su persona
 * consiente (ConsentPolicy en la API): sin esta seccion, el 400 consent_missing de la emision no tenia salida. Aqui se
 * amplia o se reduce, con las palabras de quien consiente, y queda el historial.
 *
 * Se engancha APARTE del resto de la vista y apenas existe el DOM: si la lista de llaves no carga, esta seccion
 * igual tiene que servir (es justo la que destraba la emision).
 */
function seccionConsentimientoHtml() {
  return '<div class="detail-section" style="margin-top:18px" data-consentimiento>'
    + '<h2 style="margin:0;font-size:1rem;color:' + C.texto + '">&#128272; Lo que la persona consiente</h2>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">'
    + 'Una llave solo puede pedir lo que su persona <strong>consiente</strong>. Aqui lo amplias o lo reduces con tu sesion; '
    + 'queda guardado con tus palabras, tu nombre y la fecha. Quitar un permiso no borra las llaves ya emitidas, '
    + 'pero el MCP deja de usarlo en menos de un minuto.</p>'
    + '<div style="display:flex;gap:10px;align-items:flex-end;margin-top:10px;flex-wrap:wrap">'
    + '<div style="flex:1 1 280px"><label for="ctx-c-sujeto" style="display:block;font-size:0.875rem;color:' + C.suave + ';margin-bottom:3px">Persona (subject id)</label>'
    + '<input id="ctx-c-sujeto" data-c="sujeto" placeholder="uuid de la persona" style="width:100%;box-sizing:border-box;padding:6px 8px;border-radius:6px;'
    + 'border:1px solid ' + C.linea + ';background:#0d1117;color:' + C.texto + ';font-size:0.875rem"></div>'
    + '<button type="button" data-c-ver style="padding:7px 14px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer">Ver</button>'
    + '</div>'
    + '<div data-c-aviso role="status" style="font-size:0.875rem;min-height:18px;margin:8px 0"></div>'
    + '<div data-c-cuerpo></div>'
    + '</div>';
}

function engancharConsentimiento($el) {
  const $sec = $el.querySelector('[data-consentimiento]');
  if (!$sec) return { abrir: async () => {}, devolver: () => {} };
  const $sujeto = $sec.querySelector('[data-c="sujeto"]');
  const $aviso = $sec.querySelector('[data-c-aviso]');
  const $cuerpo = $sec.querySelector('[data-c-cuerpo]');
  let actual = null; // lo que la API dice que hoy esta consentido
  // PANEL-ATAJOS-LLAVE-T1 (a): abierta desde la emision, la seccion se muda DEBAJO de la llave que se esta emitiendo y su
  // boton es "Aceptar y emitir"; al emitir vuelve a su sitio. Abierta con "Ver", es la de siempre.
  let alAceptar = null;
  const casa = { padre: $sec.parentNode, siguiente: $sec.nextSibling };
  const decir = (t, color) => { $aviso.style.color = color || C.suave; $aviso.textContent = t; };
  const marcados = () => [...$cuerpo.querySelectorAll('[data-consent]:checked')].map((c) => c.value);

  const pintarCambio = () => {
    const $c = $cuerpo.querySelector('[data-c-cambio]');
    if (!$c || !actual) return;
    const { agregar, quitar } = cambioDeConsentimiento(actual.scopes, marcados());
    $c.textContent = (!agregar.length && !quitar.length) ? 'Sin cambios todavia.'
      : (agregar.length ? 'Vas a agregar: ' + agregar.join(', ') + '. ' : '') + (quitar.length ? 'Vas a quitar: ' + quitar.join(', ') + '.' : '');
  };

  const pintar = (r, resaltar = []) => {
    actual = r;
    const cabeza = '<div style="font-size:0.875rem;color:' + C.texto + '">' + esc(r.nombre || r.sujeto)
      + (r.cambiadoEl ? ' <span style="color:' + C.suave + '">&middot; cambiado ' + esc(cuando(r.cambiadoEl) || '') + (r.cambiadoPor ? ' por ' + esc(r.cambiadoPor) : '') + '</span>' : '')
      + '</div>';
    if (r.revocado) {
      $cuerpo.innerHTML = cabeza + '<p style="font-size:0.875rem;color:' + C.ojo + '">Este consentimiento esta revocado. '
        + 'Reabrirlo es otra decision, no un cambio de permisos: por eso aqui no se puede editar.</p>';
      return;
    }
    const casillas = SCOPES.map((s) => '<label style="display:flex;gap:8px;align-items:flex-start;padding:4px 0;font-size:0.875rem;color:' + C.texto
      + (resaltar.includes(s.id) ? ';background:#d2992218;border-radius:4px' : '') + '">'
      + '<input type="checkbox" data-consent value="' + esc(s.id) + '"' + (r.scopes.includes(s.id) ? ' checked' : '') + ' style="margin-top:2px">'
      + '<span>' + esc(s.que) + '<br><span style="color:' + C.suave + ';font-size:0.875rem">' + esc(s.id) + ' &middot; riesgo ' + esc(s.riesgo) + '</span></span></label>').join('');
    const historial = r.historial.slice(0, 5).map((h) => '<li style="margin:3px 0">' + esc(cuando(h.el) || '') + ' &middot; ' + esc(h.por)
      + (h.agregados.length ? ' &middot; +' + esc(h.agregados.join(', ')) : '') + (h.quitados.length ? ' &middot; &minus;' + esc(h.quitados.join(', ')) : '')
      + (h.texto ? '<br><span style="color:' + C.suave + '">&ldquo;' + esc(h.texto) + '&rdquo;</span>' : '') + '</li>').join('');
    $cuerpo.innerHTML = cabeza
      + '<div style="margin-top:8px;columns:2 300px;column-gap:18px">' + casillas + '</div>'
      + '<label for="ctx-c-texto" style="display:block;font-size:0.875rem;color:' + C.suave + ';margin:10px 0 3px">'
      + 'Con tus palabras: que autorizas y para que (queda guardado con el cambio)</label>'
      + '<textarea id="ctx-c-texto" data-c-texto rows="2" placeholder="Ej: autorizo que el MCP de Nexo lea la bitacora, las tareas y las decisiones, y le deje solicitudes a Ares" '
      + 'style="width:100%;max-width:720px;box-sizing:border-box;padding:6px 8px;border-radius:6px;border:1px solid ' + C.linea + ';background:#0d1117;color:' + C.texto + ';font-size:0.875rem"></textarea>'
      + '<div data-c-cambio style="font-size:0.875rem;color:' + C.ojo + ';margin:6px 0"></div>'
      + '<button type="button" data-c-guardar style="padding:7px 14px;border-radius:6px;border:1px solid ' + C.linea + ';background:#1f6feb;color:#fff;font-size:0.875rem;cursor:pointer">' + (alAceptar ? 'Aceptar y emitir' : 'Guardar consentimiento') + '</button>'
      + (historial ? '<div style="margin-top:12px;font-size:0.875rem;color:' + C.texto + '">Ultimos cambios<ul style="margin:4px 0 0;padding-left:18px">' + historial + '</ul></div>'
        : '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:10px">Todavia no hay cambios registrados desde esta pantalla.</p>');
    pintarCambio();
  };

  const ver = async (resaltar = []) => {
    decir('Buscando...');
    $cuerpo.innerHTML = '';
    const r = await ContextApiClient.leerConsentimiento($sujeto.value);
    if (!r.ok) { decir(r.detalle, C.mal); return null; }
    decir('');
    pintar(r, resaltar);
    return r;
  };

  $sec.querySelector('[data-c-ver]').addEventListener('click', () => ver());
  $cuerpo.addEventListener('change', (e) => { if (e.target.hasAttribute('data-consent')) pintarCambio(); });
  $cuerpo.addEventListener('click', async (e) => {
    const $b = e.target.closest('[data-c-guardar]');
    if (!$b || !actual) return;
    const { agregar, quitar } = cambioDeConsentimiento(actual.scopes, marcados());
    const palabras = $cuerpo.querySelector('[data-c-texto]')?.value || '';
    $b.disabled = true;
    decir('Guardando...');
    const r = await ContextApiClient.cambiarConsentimiento($sujeto.value, { agregar, quitar, texto: palabras });
    $b.disabled = false;
    if (!r.ok) { decir(r.detalle, C.mal); return; }
    // EL ECO: se relee lo guardado y se compara con lo que la API dijo que quedo. Un ligado a medias no falla: calla.
    const releido = await ContextApiClient.leerConsentimiento($sujeto.value);
    const cuadra = releido.ok && r.scopes.every((s) => releido.scopes.includes(s)) && releido.scopes.length === r.scopes.length;
    if (releido.ok) pintar(releido);
    decir((r.agregados.length ? 'Agregado: ' + r.agregados.join(', ') + '. ' : '') + (r.quitados.length ? 'Quitado: ' + r.quitados.join(', ') + '. ' : '')
      + (cuadra ? 'Releido: quedo asi.' : 'Guardo, pero al releer no cuadra: vuelve a abrirlo antes de emitir.'), cuadra ? C.acento : C.ojo);
    // Abierta desde la emision: con el consentimiento guardado Y releido, se vuelve a emitir con el mismo formulario.
    if (cuadra && alAceptar) { const seguir = alAceptar; seguir(); }
  });

  // Desde el rechazo consent_missing de la emision: abre esta seccion con la persona y lo que faltaba ya marcado.
  return {
    abrir: async (sujeto, faltan = [], { debajoDe = null, alAceptar: seguir = null, texto = '' } = {}) => {
      $sujeto.value = sujeto || '';
      alAceptar = seguir;
      if (debajoDe) debajoDe.after($sec);
      $sec.scrollIntoView({ behavior: 'smooth', block: debajoDe ? 'nearest' : 'start' });
      const r = await ver(faltan);
      if (!r) return;
      $cuerpo.querySelectorAll('[data-consent]').forEach((c) => { if (faltan.includes(c.value)) c.checked = true; });
      pintarCambio();
      // LLAVES-PROPUESTAS-T1 (c): desde una propuesta de Ares el texto llega redactado; Josse lo lee y acepta o lo corrige.
      const $t = $cuerpo.querySelector('[data-c-texto]');
      if ($t && texto) $t.value = texto;
      $t?.focus();
    },
    /** La seccion vuelve a su sitio de siempre (al final de la pantalla) y su boton a "Guardar consentimiento". */
    devolver: () => {
      alAceptar = null;
      if ($sec.parentNode !== casa.padre || $sec.nextSibling !== casa.siguiente) casa.padre.insertBefore($sec, casa.siguiente);
      const $g = $cuerpo.querySelector('[data-c-guardar]');
      if ($g) $g.textContent = 'Guardar consentimiento';
    }
  };
}

/*
 * LAS LLAVES QUE PROPONE ARES (LLAVES-PROPUESTAS-T1; contrato de A #25316, nexo-api 478).
 *
 * Va ARRIBA del formulario: es lo primero que Josse tiene que decidir y casi siempre lo hace desde el telefono. Cada
 * propuesta se lee en palabras (los mismos textos de los permisos del formulario) con el motivo de Ares; Aprobar emite
 * y muestra la llave UNA vez dentro de la misma tarjeta; Rechazar pide el motivo (Ares lo lee). Si al aprobar falta el
 * consentimiento de la persona, se abre en la tarjeta con el texto REDACTADO desde la propuesta y "Aceptar y emitir".
 */
function seccionPropuestasHtml() {
  return '<div class="detail-section" style="margin-top:14px" data-propuestas>'
    + '<h2 style="margin:0;font-size:1rem;color:' + C.texto + '">&#128221; Llaves propuestas por Ares</h2>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">Ares deja lista la llave sin secreto; el secreto nace solo cuando la apruebas aqui, y se muestra una vez.</p>'
    + '<div data-propuestas-cuerpo><div style="font-size:0.875rem;color:' + C.suave + '">Buscando propuestas&hellip;</div></div>'
    + '</div>';
}

function tarjetaPropuestaHtml(p) {
  const permisos = permisosEnPalabras(p.permisos, SCOPES).map((x) => '<li style="margin:2px 0">' + esc(x.que)
    + ' <span style="font-family:monospace;font-size:0.875rem;color:' + C.suave + '">' + esc(x.id) + (x.conocido ? '' : ' &middot; este panel no lo conoce') + '</span></li>').join('');
  const btn = (attr, txt, fondo) => '<button type="button" ' + attr + ' style="padding:8px 14px;border-radius:6px;border:1px solid ' + C.linea + ';'
    + 'background:' + fondo + ';color:#fff;font-size:0.875rem;cursor:pointer;margin:4px 6px 0 0">' + txt + '</button>';
  return '<div data-propuesta="' + esc(p.id) + '" style="border:1px solid #d29922;border-radius:8px;padding:12px;margin-top:10px;background:#1c1710">'
    + '<div style="font-size:0.875rem;color:' + C.texto + '"><strong>' + esc(p.agente) + '</strong>' + (p.nombre ? ' &middot; ' + esc(p.nombre) : '') + '</div>'
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:2px">Para ' + esc(destinoDe(p)) + (p.dias ? ' &middot; ' + esc(p.dias) + ' dias' : '')
    + (p.propuestaPor ? ' &middot; la propuso ' + esc(p.propuestaPor) : '') + (p.creada ? ' ' + esc(cuando(p.creada) || '') : '') + '</div>'
    + (p.motivo ? '<div style="font-size:0.875rem;color:' + C.texto + ';margin-top:6px">&ldquo;' + esc(p.motivo) + '&rdquo;</div>' : '')
    + '<div style="font-size:0.875rem;color:' + C.suave + ';margin-top:8px">Va a poder (' + esc(p.permisos.length) + '):</div>'
    + '<ul style="margin:2px 0 0;padding-left:18px;font-size:0.875rem;color:' + C.texto + '">' + permisos + '</ul>'
    + (p.ultimoError ? '<div style="font-size:0.875rem;color:' + C.ojo + ';margin-top:6px">El ultimo intento fallo: ' + esc(p.ultimoError) + '</div>' : '')
    + '<div data-p-aviso role="status" style="font-size:0.875rem;min-height:16px;margin:8px 0 0"></div>'
    + '<div data-p-botones>' + btn('data-p-aprobar', 'Aprobar y emitir', '#238636') + btn('data-p-rechazar', 'Rechazar', '#21262d') + '</div>'
    + '<div data-p-rechazo hidden style="margin-top:8px">'
    + '<textarea data-p-motivo rows="2" placeholder="Por que la rechazas (Ares lo lee)" style="width:100%;box-sizing:border-box;padding:6px 8px;border-radius:6px;border:1px solid ' + C.linea + ';background:#0d1117;color:' + C.texto + ';font-size:0.875rem"></textarea>'
    + btn('data-p-confirmar-rechazo', 'Confirmar rechazo', '#8b2b2b') + '</div>'
    + '<div data-p-llave hidden style="margin-top:8px">'
    + '<div style="font-size:0.875rem;color:#f0c674;margin-bottom:6px">&#9888; Esta es la unica vez que veras la llave completa. Copiala ahora.</div>'
    + '<code data-p-llave-txt style="display:block;word-break:break-all;font-size:0.875rem;color:' + C.texto + ';background:#0d1117;padding:8px;border-radius:6px"></code>'
    + btn('data-p-copiar', 'Copiar', '#21262d') + '<span data-p-copiado style="font-size:0.875rem;color:' + C.bien + ';margin-left:6px"></span></div>'
    + '</div>';
}

/** Carga y engancha las propuestas. `alEmitir(fila)` suma la llave nueva a la lista de abajo cuando esa lista existe. */
function engancharPropuestas($el, consentimiento, alEmitir) {
  const $cuerpo = $el.querySelector('[data-propuestas-cuerpo]');
  if (!$cuerpo) return;
  let propuestas = [];

  const cargar = async () => {
    const r = await ContextApiClient.listarPropuestas();
    if (!r.ok) {
      // Fail-open y en gris: sin la ruta publicada (nexo-api 478) el resto de la pantalla sigue sirviendo.
      $cuerpo.innerHTML = '<div style="font-size:0.875rem;color:' + (r.motivo === 'NO_PUBLICADO' ? C.suave : C.mal) + '" data-propuestas-motivo="' + esc(r.motivo) + '">'
        + esc(r.detalle) + '</div>';
      return;
    }
    propuestas = r.propuestas;
    $cuerpo.innerHTML = propuestas.length ? propuestas.map(tarjetaPropuestaHtml).join('')
      : '<div style="font-size:0.875rem;color:' + C.suave + '" data-propuestas-vacio>Ares no tiene llaves propuestas ahora.</div>';
  };

  const aprobar = async ($t, p) => {
    const $aviso = $t.querySelector('[data-p-aviso]');
    const $botones = $t.querySelector('[data-p-botones]');
    const $b = $t.querySelector('[data-p-aprobar]');
    $b.disabled = true;
    $aviso.style.color = C.suave;
    $aviso.textContent = 'Aprobando y emitiendo...';
    const r = await ContextApiClient.aprobarPropuesta(p.id);
    $b.disabled = false;
    if (!r.ok) {
      $aviso.style.color = C.mal;
      $aviso.textContent = r.detalle || 'No se pudo aprobar.';
      if (r.motivo === 'FALTA_CONSENTIMIENTO' && p.sujeto) {
        // (c): un solo boton. Mientras el consentimiento esta abierto en la tarjeta, los botones de la propuesta se ocultan.
        $botones.hidden = true;
        consentimiento.abrir(p.sujeto, r.faltan || [], {
          debajoDe: $aviso,
          alAceptar: () => aprobar($t, p),
          texto: borradorDeConsentimiento(p, r.faltan, SCOPES)
        });
      }
      return;
    }
    consentimiento.devolver();
    $botones.hidden = true;
    $aviso.style.color = C.bien;
    $aviso.textContent = 'Aprobada: la llave quedo emitida con estos ' + p.permisos.length + ' permisos.';
    const $llave = $t.querySelector('[data-p-llave]');
    $llave.hidden = false;
    $llave.querySelector('[data-p-llave-txt]').textContent = r.llave;
    alEmitir?.({ id: r.id, agente: p.agente, nombre: p.nombre, prefijo: r.prefijo, permisos: p.permisos, sujeto: p.sujeto,
      negocio: p.negocio, creada: new Date().toISOString(), vence: r.vence || null, revocada: null, ultimoUso: null });
  };

  $cuerpo.addEventListener('click', async (e) => {
    const $t = e.target.closest('[data-propuesta]');
    if (!$t) return;
    const p = propuestas.find((x) => x.id === $t.getAttribute('data-propuesta'));
    if (!p) return;
    if (e.target.closest('[data-p-aprobar]')) { await aprobar($t, p); return; }
    if (e.target.closest('[data-p-rechazar]')) {
      $t.querySelector('[data-p-rechazo]').hidden = false;
      $t.querySelector('[data-p-motivo]')?.focus();
      return;
    }
    if (e.target.closest('[data-p-confirmar-rechazo]')) {
      const $aviso = $t.querySelector('[data-p-aviso]');
      const r = await ContextApiClient.rechazarPropuesta(p.id, $t.querySelector('[data-p-motivo]')?.value || '');
      if (!r.ok) { $aviso.style.color = C.mal; $aviso.textContent = r.detalle; return; }
      $t.querySelector('[data-p-botones]').hidden = true;
      $t.querySelector('[data-p-rechazo]').hidden = true;
      $aviso.style.color = C.suave;
      $aviso.textContent = 'Rechazada. Ares ve tu motivo.';
      return;
    }
    if (e.target.closest('[data-p-copiar]')) {
      const t = $t.querySelector('[data-p-llave-txt]')?.textContent || '';
      const $ok = $t.querySelector('[data-p-copiado]');
      try { await navigator.clipboard.writeText(t); if ($ok) $ok.textContent = 'copiada'; }
      catch { if ($ok) $ok.textContent = 'seleccionala y copiala a mano'; }
    }
  });

  cargar();
}

export async function renderContexto(container) {
  const $el = container;

  if (!puedeVer()) {
    $el.innerHTML = '<div class="detail-section">'
    + VOLVER
      + '<h1 style="margin:0;font-size:1.1rem;color:' + C.texto + '">&#128273; Accesos de asistentes</h1>'
      + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:10px">'
      + 'Esta pantalla es para el dueno del contexto y para los devs. Tu usuario entra como <strong>'
      + esc(AppConfig.role) + '</strong>.</p></div>';
    return;
  }

  $el.innerHTML = '<div class="detail-section">'
    + VOLVER
    + '<h1 style="margin:0;font-size:1.1rem;color:' + C.texto + '">&#128273; Accesos de asistentes</h1>'
    + '<p style="font-size:0.875rem;color:' + C.suave + ';margin-top:4px">'
    + 'Quien tiene una llave para leer tu contexto, que puede hacer con ella y cuando la uso por ultima vez. '
    + 'La llave completa no se muestra nunca: solo su comienzo.</p>'
    + '<button data-abrir-form style="margin-top:10px;padding:6px 12px;border-radius:6px;'
    + 'border:1px solid ' + C.linea + ';background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer">'
    + '+ Dar acceso a un asistente</button>'
    + '</div>'
    + seccionPropuestasHtml()
    + formularioHtml()
    + '<div data-ctx-cuerpo style="margin-top:16px">'
    + '<div style="padding:30px;text-align:center;color:' + C.suave + ';font-size:0.875rem">Buscando las llaves&hellip;</div></div>'
    // CTX-M2-10 va DEBAJO y no en otra ruta: es el otro lado del mismo tablero.
    // Arriba se decide QUE puede hacer un asistente; abajo, si uno le cree lo
    // que dijo. Las dos son decisiones sobre asistentes, y por eso siguen
    // juntas.
    //
    // Lo que si se separo (CTX-P2) es el PROGRESO: "como va esta persona" se
    // volvio una pregunta de Usuarios, no de esta pantalla. Aqui se decide;
    // alla se mira.
    + '</div>' + seccionConsentimientoHtml() + seccionValidarHtml();

  // Antes de pedir las llaves: si esa lista falla, el consentimiento igual tiene que servir.
  const consentimiento = engancharConsentimiento($el);
  // Las propuestas tampoco dependen de la lista de llaves; cuando la lista existe, la llave aprobada se suma abajo.
  let alEmitirLlave = null;
  engancharPropuestas($el, consentimiento, (fila) => alEmitirLlave?.(fila));

  const $cuerpo = $el.querySelector('[data-ctx-cuerpo]');
  const res = await ContextApiClient.listCredentials();

  if (!res.ok) {
    $cuerpo.innerHTML = pantallaSinDatos(res.motivo, res.detalle);
    return;
  }

  const miId = getJwtUserId();
  const creds = res.credenciales;

  // PANEL-ATAJOS-LLAVE-T1 (b): las quitadas y vencidas no cargan en la vista principal; "Ver archivadas" las trae.
  let verArchivadas = false;
  const pintar = () => {
    const { visibles, archivadas } = llavesVisibles(creds, verArchivadas, (c) => estadoDe(c).viva);
    const vacio = creds.length === 0 ? 'Ningun asistente tiene llave todavia.'
      : 'Ninguna llave activa. Las ' + archivadas + ' quitadas o vencidas estan archivadas.';
    $cuerpo.innerHTML = (visibles.length === 0
      ? '<div style="padding:26px;text-align:center;border:1px solid ' + C.linea + ';border-radius:8px;color:' + C.suave + ';font-size:0.875rem">'
        + esc(vacio) + '</div>'
      : visibles.map((c) => filaHtml(c, miId)).join(''))
      + (archivadas ? '<button type="button" data-ver-archivadas style="margin-top:10px;padding:5px 10px;border-radius:6px;border:1px solid ' + C.linea + ';'
        + 'background:#21262d;color:' + C.texto + ';font-size:0.875rem;cursor:pointer">'
        + (verArchivadas ? 'Ocultar archivadas' : 'Ver archivadas (' + archivadas + ')') + '</button>' : '')
      + '<div style="color:' + C.suave + ';font-size:0.875rem;margin-top:14px;border-top:1px solid ' + C.linea + ';padding-top:10px">'
      + esc(creds.length) + ' llave(s) emitidas, ' + esc(creds.length - archivadas) + ' activa(s). '
      + 'Esta lista trae TODAS las llaves del sistema, no solo las tuyas: el panel todavia no sabe reconocerte por el token. '
      + 'Cuando lo sepa, se filtrara sola.</div>';
    enganchar();
  };

  alEmitirLlave = (fila) => { creds.push(fila); pintar(); };

  const enganchar = () => {
    $cuerpo.querySelector('[data-ver-archivadas]')?.addEventListener('click', () => { verArchivadas = !verArchivadas; pintar(); });
    $cuerpo.querySelectorAll('[data-quitar]').forEach((b) => {
      b.addEventListener('click', () => {
        const id = b.getAttribute('data-quitar');
        const caja = $cuerpo.querySelector('[data-quitar-caja="' + id + '"]');
        if (caja) { caja.hidden = false; caja.querySelector('[data-motivo]')?.focus(); }
        b.hidden = true;
      });
    });
    $cuerpo.querySelectorAll('[data-cancelar]').forEach((b) => {
      b.addEventListener('click', () => {
        const id = b.getAttribute('data-cancelar');
        const caja = $cuerpo.querySelector('[data-quitar-caja="' + id + '"]');
        if (caja) caja.hidden = true;
        const boton = $cuerpo.querySelector('[data-quitar="' + id + '"]');
        if (boton) boton.hidden = false;
      });
    });
    $cuerpo.querySelectorAll('[data-confirmar]').forEach((b) => {
      b.addEventListener('click', async () => {
        const id = b.getAttribute('data-confirmar');
        const $motivo = $cuerpo.querySelector('[data-motivo="' + id + '"]');
        const $aviso = $cuerpo.querySelector('[data-aviso="' + id + '"]');
        const motivo = ($motivo?.value || '').trim();
        if (!motivo) { if ($aviso) $aviso.textContent = 'Escribe por que se la quitas.'; return; }
        b.disabled = true;
        if ($aviso) { $aviso.style.color = C.suave; $aviso.textContent = 'Quitando...'; }
        const r = await ContextApiClient.revokeCredential(id, motivo);
        b.disabled = false;
        if (!r.ok) {
          if ($aviso) { $aviso.style.color = C.mal; $aviso.textContent = r.detalle || 'No se pudo quitar.'; }
          return;
        }
        // Se refleja lo que quedo, no lo que se pidio: la fila se marca revocada
        // con la fecha de ahora y se vuelve a pintar desde el mismo estado.
        const fila = creds.find((c) => c.id === id);
        if (fila) fila.revocada = new Date().toISOString();
        pintar();
      });
    });
  };

  // EL ENGANCHE VA AQUI, NO ARRIBA, Y ESTO NO ES ESTILO.
  //
  // Estos manejadores usan `creds` y `pintar`, que se declaran con const mas
  // arriba PERO despues del await. Si el enganche viviera junto al innerHTML,
  // un clic antes de que respondan las llaves caeria en la zona muerta y
  // reventaria; y si listCredentials falla, la funcion vuelve antes de
  // declararlas y el boton quedaria enganchado a nada.
  //
  // Es el mismo tropiezo que me costo una vista en blanco esta manana con una
  // constante dentro del closure. Aqui se engancha cuando todo existe.
  // --- dar acceso -------------------------------------------------------------
  const $form = $el.querySelector('[data-ctx-form]');
  const $nueva = $el.querySelector('[data-llave-nueva]');

  // --- la receta y el aviso del conjunto (CTX-M2-20) --------------------------
  //
  // El aviso vive en el MISMO sitio donde despues sale el error de la API
  // (data-form-aviso), a proposito: si apareciera en otro lado, quien emite
  // tendria dos lugares que mirar y terminaria mirando ninguno.
  const $avisoForm = $form?.querySelector('[data-form-aviso]');
  const marcados = () => [...$form.querySelectorAll('[data-scope]:checked')].map((c) => c.value);

  const revisar = () => {
    if (!$avisoForm) return;
    const r = revisarConjunto(marcados());
    if (!r) { $avisoForm.textContent = ''; return; }
    // Amarillo y no rojo: NO bloquea. Hay agentes legitimos con una sola de las
    // dos grafias -- la llave de Q es una --, asi que esto avisa, no impide.
    $avisoForm.style.color = '#f0c674';
    $avisoForm.textContent = '\u26A0 ' + r.texto;
  };

  const $resumen = $form?.querySelector('[data-resumen-permisos]');
  const pintarResumen = () => { if ($resumen) $resumen.textContent = resumenDePermisos(marcados()); };

  $form?.addEventListener('change', (e) => {
    if (e.target && e.target.hasAttribute('data-scope')) { revisar(); pintarResumen(); }
  });

  $form?.querySelectorAll('[data-receta]').forEach(($b) => {
    $b.addEventListener('click', () => {
      const receta = RECETAS.find((r) => r.id === $b.getAttribute('data-receta'));
      if (!receta) return;
      // PANEL-ATAJOS-LLAVE-T1 (Josse 11-oct): el atajo SUMA a lo marcado. Antes dejaba EXACTAMENTE los suyos (CTX-M2-20)
      // y marcar MCP borraba lo del tutor: una llave con las dos cosas no se podia armar. Borrar es "Limpiar permisos".
      const antes = marcados();
      const nuevos = cuantosNuevos(antes, receta);
      const quedan = sumarReceta(antes, receta);
      $form.querySelectorAll('[data-scope]').forEach((c) => { c.checked = quedan.includes(c.value); });
      revisar();
      pintarResumen();
      if ($avisoForm && !revisarConjunto(quedan)) {
        $avisoForm.style.color = C.bien;
        $avisoForm.textContent = '"' + receta.nombre + '": ' + (nuevos ? 'agregue ' + nuevos + ' permiso' + (nuevos === 1 ? '' : 's') : 'ya estaban todos')
          + '. Lo que ya tenias marcado sigue marcado.';
      }
    });
  });

  $form?.querySelector('[data-limpiar-permisos]')?.addEventListener('click', () => {
    $form.querySelectorAll('[data-scope]').forEach((c) => { c.checked = false; });
    revisar();
    pintarResumen();
  });

  $el.querySelector('[data-abrir-form]')?.addEventListener('click', (e) => {
    $form.hidden = !$form.hidden;
    e.target.textContent = $form.hidden ? '+ Dar acceso a un asistente' : 'Cancelar';
    // Cancelar con el consentimiento abierto en la tarjeta: la seccion vuelve a su sitio y "Emitir llave" reaparece.
    if ($form.hidden) {
      consentimiento.devolver();
      const $emitir = $form.querySelector('[data-emitir]');
      if ($emitir) $emitir.hidden = false;
    }
    if (!$form.hidden) $form.querySelector('[data-f="agente"]')?.focus();
  });
  $el.querySelector('[data-emitir]')?.addEventListener('click', async (e) => {
    const $aviso = $form.querySelector('[data-form-aviso]');
    const val = (n) => $form.querySelector('[data-f="' + n + '"]')?.value || '';
    const scopes = [...$form.querySelectorAll('[data-scope]:checked')].map((c) => c.value);

    // SE VALIDA ANTES DE GASTAR EL VIAJE, y se dice QUE falta -- no "revisa los
    // datos", que obliga a adivinar cual. Mismo trato que ya recibe el motivo
    // al revocar (CTX-M2-22).
    const donde = val('donde').trim();
    if (!donde) {
      $aviso.style.color = C.mal;
      $aviso.textContent = 'Falta decir donde queda el secreto. La llave se muestra una sola vez: sin eso, el acceso queda vivo y sin dueno.';
      return;
    }
    // Y SI LO QUE ESCRIBIERON PARECE EL SECRETO, NO SE GUARDA.
    //
    // El aviso de arriba explica por que no hay que pegarlo; esto lo impide
    // cuando alguien lo pega igual, que es lo que pasa con las advertencias.
    // Falla RUIDOSO: no lo recorta ni lo enmascara en silencio, porque un
    // secreto a medias publicado sigue publicado y ademas nadie se entera.
    //
    // CALIBRADO antes de cablearlo, contra siete formas reales de contestar
    // -- "gestor de claves de Josse", una ruta de Windows, "el .env del
    // contenedor", "se lo mande por WhatsApp" -- y cero dieron falso positivo;
    // y contra cinco secretos de verdad -- nxk_, sk-, ghp_, un JWT y una cadena
    // suelta de 28 caracteres --, y los cinco los caza.
    if (pareceUnSecreto(donde)) {
      $aviso.textContent = 'Eso parece el secreto, no el sitio donde quedo. No lo guardo: este campo '
        + 'termina en la bitacora, que se lee sin credencial. Escribi donde lo dejaste, no que es.';
      return;
    }

    e.target.disabled = true;
    $aviso.style.color = C.suave;
    $aviso.textContent = 'Emitiendo...';

    // DONDE SE GUARDA, Y POR QUE AHI. La tarea deja la decision a quien la
    // construya, con un criterio: el dato tiene que poder LEERSE despues desde
    // la lista, no solo escribirse.
    //
    // Va dentro de `name`, que ya existe, ya viaja en createCredential y ya se
    // pinta en cada fila de la lista. Medido antes de decidir: la columna es
    // `text` sin limite, y los nombres YA usan este mismo separador -- la llave
    // del tutor se llama "GPT Tutor de ingles (Josse) - M2: abre sesion, anota
    // y cierra" --, asi que esto sigue la convencion de la casa en vez de
    // romperla.
    //
    // LO QUE CUESTA, dicho en voz alta: ensucia el nombre. Lo limpio seria una
    // columna propia -- p. ej. secret_location -- y eso toca la API, que no es
    // mi territorio en esta tarea. Queda propuesto: mientras no exista, esto se
    // lee desde la lista HOY, que es lo que la tarea pedia.
    const nombreConRastro = `${val('nombre').trim()} — secreto en: ${donde}`.trim();

    const r = await ContextApiClient.createCredential({
      agente: val('agente'), nombre: nombreConRastro, sujeto: val('sujeto'),
      negocio: val('negocio'), dias: val('dias'), scopes
    });
    e.target.disabled = false;
    if (!r.ok) {
      $aviso.style.color = C.mal;
      $aviso.textContent = r.detalle || 'No se pudo emitir.';
      // El 400 consent_missing ya no es un callejon. PANEL-ATAJOS-LLAVE-T1 (a), Josse 11-oct: el cuadro de ampliar el
      // consentimiento salia al final de la pantalla, lejos de la llave; ahora se abre AQUI, en la misma tarjeta, con lo
      // que faltaba marcado, y su boton "Aceptar y emitir" guarda el consentimiento y vuelve a emitir esta llave.
      if (r.motivo === 'FALTA_CONSENTIMIENTO' && val('sujeto').trim()) {
        // Un solo boton azul: mientras el consentimiento esta abierto aqui, "Emitir llave" se oculta (medido en el
        // telefono: los dos seguidos no dicen cual tocar). Vuelve al emitir o al cancelar.
        e.target.hidden = true;
        consentimiento.abrir(val('sujeto').trim(), r.faltan || [], {
          debajoDe: $aviso,
          alAceptar: () => $el.querySelector('[data-emitir]')?.click()
        });
      }
      return;
    }
    // La llave viaja una sola vez: se pinta y NO se guarda en ninguna parte.
    consentimiento.devolver();
    e.target.hidden = false;
    $aviso.textContent = '';
    $form.hidden = true;
    const $btn = $el.querySelector('[data-abrir-form]');
    if ($btn) $btn.textContent = '+ Dar acceso a un asistente';
    $nueva.hidden = false;
    $nueva.querySelector('[data-llave-txt]').textContent = r.llave;
    creds.push({ id: r.id, agente: val('agente'), nombre: val('nombre'), prefijo: r.prefijo,
      permisos: scopes, sujeto: val('sujeto'), negocio: val('negocio'),
      creada: new Date().toISOString(), vence: null, revocada: null, ultimoUso: null });
    pintar();
  });
  $el.querySelector('[data-copiar]')?.addEventListener('click', async () => {
    const t = $nueva.querySelector('[data-llave-txt]')?.textContent || '';
    const $ok = $nueva.querySelector('[data-copiado]');
    try { await navigator.clipboard.writeText(t); if ($ok) $ok.textContent = 'copiada'; }
    catch { if ($ok) $ok.textContent = 'seleccionala y copiala a mano'; }
  });

  // --- CTX-M2-10: ver lo que el asistente cree, y decidir ---------------------
  //
  // Se engancha aqui abajo, con el resto, y no junto al innerHTML: estos
  // manejadores no dependen de `creds`, pero si de que el DOM exista, y
  // mantenerlos todos en el mismo sitio evita que el proximo tenga que buscar
  // en dos lados por que un boton no responde.
  const $vAviso = $el.querySelector('[data-v-aviso]');
  const $vCuerpo = $el.querySelector('[data-v-cuerpo]');
  const decir = (txt, color) => { if ($vAviso) { $vAviso.style.color = color || C.suave; $vAviso.textContent = txt; } };
  const valorV = (n) => $el.querySelector('[data-v="' + n + '"]')?.value || '';

  const pintarPerfil = (r) => {
    const cabecera = perfilHtml(r.perfil);
    if (!r.pendientes.length) {
      // UN VACIO MUDO SE LEE COMO "esta roto". Se dice POR QUE esta vacio.
      $vCuerpo.innerHTML = cabecera
        + '<p style="font-size:0.875rem;color:' + C.suave + '">'
        + 'Nada espera tu decision ahora mismo. Cuando un asistente registre una observacion nueva, aparece aqui.</p>';
      return;
    }
    $vCuerpo.innerHTML = cabecera
      + '<div style="font-size:0.875rem;color:' + C.suave + ';margin:10px 0 6px">'
      + 'Esperan tu decision (' + r.pendientes.length + ')</div>'
      + r.pendientes.map(pendienteHtml).join('')
      + '<button data-v-guardar style="margin-top:6px;padding:7px 14px;border-radius:6px;'
      + 'border:1px solid ' + C.linea + ';background:#1f6feb;color:#fff;font-size:0.875rem;cursor:pointer">'
      + 'Guardar decisiones</button>';
  };

  $el.querySelector('[data-v-ver]')?.addEventListener('click', async (e) => {
    e.target.disabled = true;
    decir('Buscando...');
    $vCuerpo.innerHTML = '';
    const r = await LearningProfileApiClient.getProfile({ sujeto: valorV('sujeto'), programa: valorV('programa') });
    e.target.disabled = false;
    if (!r.ok) { decir(r.detalle, C.mal); return; }
    decir('');
    pintarPerfil(r);
  });

  // Un clic elige, y se VE cual quedo elegido: sin eso hay que acordarse de lo
  // que uno toco, y aqui se estan decidiendo cosas sobre una persona.
  $vCuerpo?.addEventListener('click', (ev) => {
    const $b = ev.target.closest('[data-dec]');
    if (!$b) return;
    const $fila = $b.closest('[data-obs]');
    const elegida = $b.getAttribute('data-dec');
    const yaEstaba = $fila.getAttribute('data-elegida') === elegida;
    $fila.setAttribute('data-elegida', yaEstaba ? '' : elegida);   // segundo clic = deshacer
    $fila.querySelectorAll('[data-dec]').forEach(($x) => {
      const activo = !yaEstaba && $x.getAttribute('data-dec') === elegida;
      $x.style.background = activo ? (elegida === 'accept' ? '#1f6feb' : '#8b2c2c') : '#21262d';
      $x.style.color = activo ? '#fff' : C.texto;
    });
    // La nota aparece al rechazar: es donde de verdad hace falta explicar.
    const $nota = $fila.querySelector('[data-nota]');
    if ($nota) $nota.hidden = yaEstaba || elegida !== 'reject';
  });

  $vCuerpo?.addEventListener('click', async (ev) => {
    const $g = ev.target.closest('[data-v-guardar]');
    if (!$g) return;
    const decisiones = [...$vCuerpo.querySelectorAll('[data-obs]')]
      .map(($f) => ({
        id: $f.getAttribute('data-obs'),
        decision: $f.getAttribute('data-elegida') || '',
        nota: $f.querySelector('[data-nota]')?.value || ''
      }))
      .filter((d) => d.decision === 'accept' || d.decision === 'reject');

    if (!decisiones.length) { decir('No marcaste ninguna. Elegi aceptar o rechazar en al menos una.', '#f0c674'); return; }

    // RECHAZAR EXIGE NOTA (CTX-M2-22, segunda mitad, hallazgo de Argy-Q #10827).
    //
    // ES LA MISMA ASIMETRIA EN OTRA DIRECCION, y Q la dijo mejor de lo que yo
    // la diria: para QUITAR UNA LLAVE esta pantalla exige motivo -- "sin motivo
    // no queda rastro util" --; para RECHAZAR LO QUE UN ASISTENTE CREE DE UNA
    // PERSONA no exigia nada. Si el rastro importa para una credencial, importa
    // MAS para una decision sobre alguien.
    //
    // Y ERA MI PROPIO CODIGO: yo recogia la nota con `?.value || ''`, o sea que
    // la cadena vacia pasaba entera. La API tampoco frena -- ValidateProfileRequest
    // declara Note anulable y sin [Required] --, asi que se podia rechazar en
    // blanco y no se quejaba nadie. Aqui se frena en el panel; ponerle
    // [Required] al DTO cuando la decision sea rechazo es de la API, y lo dejo
    // propuesto.
    //
    // ACEPTAR NO LA EXIGE, y es a proposito: aceptar es estar de acuerdo con lo
    // que ya esta escrito y medido. Rechazar es CONTRADECIR a alguien, y eso es
    // lo que necesita quedar explicado.
    const sinNota = decisiones.filter((d) => d.decision === 'reject' && !String(d.nota).trim());
    if (sinNota.length) {
      decir(sinNota.length === 1
        ? 'Falta decir por que rechazas esa observacion: sin nota no queda rastro de tu criterio.'
        : `Faltan ${sinNota.length} notas: rechazar sin decir por que no deja rastro de tu criterio.`, C.mal);
      return;
    }

    $g.disabled = true;
    decir('Guardando...');
    const r = await LearningProfileApiClient.validate({
      sujeto: valorV('sujeto'), programa: valorV('programa'), decisiones
    });
    $g.disabled = false;
    if (!r.ok) { decir(r.detalle, C.mal); return; }
    // Se dice lo que PASO con numeros, incluida la version: es el rastro que la
    // tarea pedia, y es lo unico que prueba que la decision quedo guardada.
    decir('Listo: ' + r.aceptadas + ' aceptadas, ' + r.rechazadas + ' rechazadas'
      + (r.superseded ? ', ' + r.superseded + ' reemplazadas' : '')
      + '. Perfil version ' + r.versionAnterior + ' → ' + r.versionNueva + '.', C.bien);
    // Y se vuelve a leer del servidor, no se pinta lo que yo creo que quedo:
    // el eco es lo unico que confirma que se guardo lo que el usuario vio.
    const rel = await LearningProfileApiClient.getProfile({ sujeto: valorV('sujeto'), programa: valorV('programa') });
    if (rel.ok) pintarPerfil(rel);
  });

  pintar();
}


/**
 * Parece esto un secreto en vez de un SITIO donde quedo el secreto?
 *
 * Dos senales, y las dos acotadas a proposito para no acusar a quien contesta
 * bien: un prefijo de llave conocido, o una sola palabra larga con mezcla de
 * mayusculas, minusculas y digitos. Cualquier cosa con espacios o con una barra
 * de ruta se considera una respuesta legitima -- que es como se contesta de
 * verdad: "gestor de claves de Josse", "el .env del contenedor".
 * SE EXPORTA para poder probarla sola. Un detector que no se puede ejercitar
 * aparte se prueba a traves de la pantalla, y ahi cualquier fallo de la sonda
 * se lee como un fallo del detector -- me acaba de pasar al medirlo.
 */
export function pareceUnSecreto(texto) {
  const s = String(texto || '').trim();
  if (!s) return false;
  if (/(^|\s)(nxk_|sk-|ghp_|gho_|xoxb-|xoxp-|AKIA|eyJ[A-Za-z0-9_-]{6,})/.test(s)) return true;
  if (/\s/.test(s) || s.includes('/') || s.includes('\\')) return false;
  return s.length >= 24 && /[a-z]/.test(s) && /[A-Z]/.test(s) && /[0-9]/.test(s);
}